import { motion } from "framer-motion";
import { ArrowRight, Layout, Edit, Zap, Palette } from "lucide-react";
import SEO from "../components/SEO";

export default function WebsiteBuilder() {
    return (
        <div className="min-h-screen bg-base text-primary">
            <SEO
                title="Best AI Website Builder | No-Code Drag & Drop Site Creator"
                description="Build stunning, SEO-optimized websites in minutes with blinqbooks — the best AI website builder in 2026. No coding required. Drag & drop editor, free templates, custom domains & built-in analytics. Start free."
            />

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-bold uppercase mb-6">
                            <Zap className="w-4 h-4" />
                            No-Code Digital Presence
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight">
                            Build Your Brand <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-500">In 60 Seconds</span>
                        </h1>
                        <p className="text-lg text-secondary dark:text-gray-300 mb-8 max-w-lg leading-relaxed">
                            Generate a professional website tailored to your business instantly. No coding, no designer, just your ideas and our AI.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button className="btn-primary w-full sm:w-auto px-8 py-4 shadow-lg text-lg">
                                Build Your Site
                            </button>
                            <button className="btn-secondary w-full sm:w-auto px-8 py-4 shadow-sm text-lg">
                                View Templates
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative"
                    >
                        {/* Ambient glow */}
                        <div className="absolute inset-0 bg-purple-500/20 dark:bg-purple-500/10 blur-[100px] rounded-full pointer-events-none"></div>
                        
                        {/* Browser Window Wrapper */}
                        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl p-0 overflow-hidden flex flex-col h-[400px]">
                            
                            {/* Browser Top Bar */}
                            <div className="bg-slate-500/10 dark:bg-slate-955 px-4 py-3 flex items-center justify-between">
                                <div className="flex gap-2 items-center">
                                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                                    <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                                </div>
                                <div className="w-1/2 h-5 bg-slate-500/10 dark:bg-slate-900/40 rounded-md flex items-center justify-center">
                                    <div className="w-24 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                                </div>
                                <div className="flex gap-1.5">
                                    <div className="w-4 h-4 rounded bg-slate-200 dark:bg-slate-800"></div>
                                </div>
                            </div>

                            {/* Website Builder App UI */}
                            <div className="flex flex-1 overflow-hidden">
                                
                                {/* Left Sidebar (Tools) */}
                                <div className="w-20 md:w-24 bg-slate-500/5 dark:bg-slate-900/20 flex flex-col items-center py-4 gap-4">
                                    {[Layout, Edit, Palette, Zap].map((Icon, i) => (
                                        <div key={i} className="p-2.5 rounded-xl bg-white/50 dark:bg-slate-800/50 text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 cursor-pointer transition-all">
                                            <Icon className="w-5 h-5 flex-shrink-0" />
                                        </div>
                                    ))}
                                </div>

                                {/* Main Canvas */}
                                <div className="flex-1 bg-slate-100/50 dark:bg-[#0a0f1c] p-6 flex flex-col">
                                    {/* Built Website Header Simulation */}
                                    <div className="w-full bg-white/50 dark:bg-slate-800/50 rounded-t-xl p-4 flex items-center justify-between">
                                        <div className="w-16 h-3 bg-slate-200 dark:bg-slate-600 rounded"></div>
                                        <div className="flex gap-3">
                                            <div className="w-8 h-2 bg-slate-100 dark:bg-slate-700 rounded"></div>
                                            <div className="w-8 h-2 bg-slate-100 dark:bg-slate-700 rounded"></div>
                                            <div className="w-8 h-2 bg-white dark:bg-slate-600 rounded"></div>
                                        </div>
                                    </div>
                                    
                                    {/* Built Website Hero Simulation */}
                                    <div className="w-full flex-1 bg-purple-50/50 dark:bg-purple-900/10 rounded-b-xl p-8 flex flex-col items-center justify-center text-center relative overflow-hidden group">
                                        <div className="absolute inset-0 border-2 border-purple-400/0 group-hover:border-purple-400/50 transition-colors z-10 pointer-events-none"></div>
                                        <div className="w-12 h-12 bg-purple-200 dark:bg-purple-500/30 rounded-full mb-4"></div>
                                        <div className="w-3/4 h-5 bg-purple-300 dark:bg-purple-500/40 rounded-full mb-3"></div>
                                        <div className="w-1/2 h-3 bg-purple-200 dark:bg-purple-500/20 rounded-full mb-6"></div>
                                        <div className="px-6 py-2 bg-purple-500 rounded-lg w-24 h-8"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Template Preview Slider / Grid */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 text-primary dark:text-white">Smart Design, zero effort</h2>
                        <p className="text-secondary dark:text-gray-300">Choose from hundreds of conversion-optimized templates.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: "Modern Portfolio", icon: <Edit className="text-blue-500" />, tag: "Creative", image: "/assets/portfolio_template.png?v=1" },
                            { title: "E-Commerce", icon: <Layout className="text-purple-500" />, tag: "Business", image: "/assets/ecommerce_template.png?v=1" },
                            { title: "Landing Page", icon: <Palette className="text-emerald-500" />, tag: "Marketing", image: "/assets/landing_page_template.png?v=2" },
                        ].map((t, i) => (
                            <div key={i} className="group cursor-pointer">
                                <div className="aspect-video bg-slate-500/10 dark:bg-slate-800/50 rounded-2xl mb-4 overflow-hidden transition-all group-hover:scale-[1.01] relative flex items-center justify-center">
                                    <img src={t.image} alt={t.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/10 transition-colors duration-300"></div>
                                    <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2">
                                        <div className="p-2 bg-white/20 backdrop-blur-md rounded-lg hidden group-hover:block transition-all">
                                            {t.icon}
                                        </div>
                                        <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold text-white uppercase tracking-wider">{t.tag}</span>
                                    </div>
                                </div>
                                <h3 className="font-bold text-lg dark:text-white">{t.title}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-indigo-600 dark:bg-accent rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Start building for free</h2>
                    <p className="text-indigo-100 mb-8 text-lg relative z-10">Go live in minutes, not months.</p>
                    <button className="px-10 py-4 bg-white text-indigo-600 dark:text-accent rounded-xl font-bold hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto relative z-10 shadow-xl">
                        Launch Your Site <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </section>
        </div>
    );
}
